import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { subwayApi } from '../api/streetsApi'

/**
 * Subway Modal Component
 * Form for creating/editing subway stations
 */
export function SubwayModal({ isOpen, onClose, subway }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!subway

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subway: '',
      city: null,
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (subway) {
      reset({
        subway: subway.subway || '',
        city: subway.city ? { id: subway.city_id, name: subway.city_name } : null,
      })
    } else {
      reset({ subway: '', city: null })
    }
  }, [subway, reset])

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      const data = {
        subway: formData.subway,
        city: formData.city?.id,
      }
      return isEdit ? subwayApi.update(subway.id, data) : subwayApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subway'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit ? t('streets.subwayUpdated') : t('streets.subwayCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error?.message || t('streets.saveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('streets.editSubway') : t('streets.createSubway')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="subway"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('streets.subwayStation')} *`}
                      error={errors.subway?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={`${t('streets.city')} *`}
                      placeholder={t('streets.selectCity')}
                      fetchOptions={geolocateApi.getCitiesAutocomplete}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      error={errors.city?.message}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              loading={saveMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}
